import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AccountTab from '../Settings/AccountTab';
import NotificationsTab from '../Settings/NotificationsTab';
import SecurityTab from '../Settings/SecurityTab';
import BillingTab from '../Settings/BillingTab';
import './GlobalHeader.css';

const GlobalHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null); // 'account', 'notifications', 'security'
  const dropdownRef = useRef(null);

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await signOut();
    setIsDropdownOpen(false);
    navigate('/login');
  };

  return (
    <>
      <nav className="global-navbar">
        {/* Left: Logo */}
        <div className="nav-left">
          <div className="nav-logo" onClick={() => navigate('/')}>
            TheClawGen
          </div>
        </div>

        {/* Center: Navigation Pill */}
        <div className="nav-center">
          <div className="nav-pill-container">
            <Link to="/" className={`nav-pill-item ${isActive('/') ? 'active' : ''}`}>
              Home
            </Link>
            <Link to="/agents" className={`nav-pill-item ${isActive('/agents') ? 'active' : ''}`}>
              Agents
            </Link>
            <Link to="/dashboard" className={`nav-pill-item ${isActive('/dashboard') ? 'active' : ''}`}>
              Dashboard
            </Link>
            <a href="/#features" className="nav-pill-item">
              About
            </a>
          </div>
        </div>

        {/* Right: Account Dropdown Trigger */}
        <div className="nav-right" ref={dropdownRef}>
          <div className="account-dropdown-wrapper">
            <button 
              className={`nav-account-btn ${isDropdownOpen ? 'active' : ''}`}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <div className="avatar-mini">
                {user?.email?.[0].toUpperCase() || 'U'}
              </div>
              <span className="account-text">Account</span>
              <svg 
                width="12" height="12" viewBox="0 0 24 24" fill="none" 
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                className={`dropdown-chevron ${isDropdownOpen ? 'open' : ''}`}
              >
                <path d="m6 9 6 6 6-6"/>
              </svg>
            </button>

            {isDropdownOpen && (
              <div className="account-dropdown-menu">
                <div className="dropdown-header">
                  <div className="user-profile-img">
                    {user?.email?.[0].toUpperCase() || 'U'}
                  </div>
                  <div className="user-profile-info">
                    <div className="user-name">{user?.email?.split('@')[0]}</div>
                    <div className="user-email">{user?.email}</div>
                  </div>
                </div>

                <div className="dropdown-divider" />

                <div className="dropdown-links">
                  <button className="dropdown-item" onClick={() => { setActiveModal('account'); setIsDropdownOpen(false); }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    Manage Account
                  </button>
                  <button className="dropdown-item" onClick={() => { setActiveModal('notifications'); setIsDropdownOpen(false); }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>
                    Notifications
                  </button>
                  <button className="dropdown-item" onClick={() => { setActiveModal('billing'); setIsDropdownOpen(false); }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                    Billing & Plans
                  </button>
                  <button className="dropdown-item" onClick={() => { setActiveModal('security'); setIsDropdownOpen(false); }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                    Privacy & Security
                  </button>
                </div>

                <div className="dropdown-divider" />

                <button className="dropdown-item logout" onClick={handleLogout}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Settings Modal Overlay */}
      {activeModal && (
        <div className="settings-modal-overlay" onClick={() => setActiveModal(null)}>
          <div className="settings-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-nav">
              <h2>
                {activeModal === 'account' && 'Account Settings'}
                {activeModal === 'billing' && 'Billing & Plans'}
                {activeModal === 'notifications' && 'Notification Preferences'}
                {activeModal === 'security' && 'Security & Access'}
              </h2>
              <button className="modal-close-btn" onClick={() => setActiveModal(null)}>&times;</button>
            </div>
            <div className="modal-body-scroll">
              {activeModal === 'account' && <AccountTab user={user} />}
              {activeModal === 'billing' && <BillingTab />}
              {activeModal === 'notifications' && <NotificationsTab />}
              {activeModal === 'security' && <SecurityTab />}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalHeader;
